import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useCallback } from "react";
import { propEq, pipe, curry, map, keys, equals, pluck } from "ramda";
import StackedBarchart from "@dataVisualizations/stackedBarchart/StackedBarchart";
import { getChildOrganizations } from "@webapi/api";
import { useMeasurementsWithMetadata } from "@webapi/hooks/useMeasurementsWithMetadata";
import { useUnitListsContext, removeUnitsFromSelectedList, copyUnitsIntoVolatileList, getVolatileId, selectUnitList } from "@unitLists";
import { useAuthContext } from "@authentication/context";
import { measurementByPeriod, extractValue, measureCodeExplanations } from "utils/measurement";
import { getPeriod, arrayAnnotationDate, truncatedPeriodAnnotation } from "utils/periods";
import { dateConvertMeasurements, measurementsParams } from "utils/measurementsWithMetadata";
import { getGender, allGenderTypes } from "utils/genders";
import { getColorUsingOption } from "utils/colors";
import { sort } from "utils/sort";
import { sortGraphData } from "utils/graphs";
import { jsx as _jsx } from "react/jsx-runtime";
export default function StackedBarchartWrapper(props) {
  var id = props.id,
    query = props.query,
    setQuery = props.setQuery,
    measure = props.measure,
    lookupOrganization = props.lookupOrganization;
  var _useAuthContext = useAuthContext(),
    getUser = _useAuthContext.getUser;
  var authTicket = getUser().ticket;
  var _useMeasurementsWithM = useMeasurementsWithMetadata(measurementsParams(query)),
    measurementsWithMetadata = _useMeasurementsWithM.measurementsWithMetadata,
    loading = _useMeasurementsWithM.loading;
  var measureMeasurementsWithMetadata = measurementsWithMetadata.get(measure.measureId) || [];
  var _useUnitListsContext = useUnitListsContext(),
    activeUnitList = _useUnitListsContext.activeUnitList,
    setUnitLists = _useUnitListsContext.setUnitLists;
  var unitListIdx = function unitListIdx(id) {
    return query.units.indexOf(id);
  };
  var orgMeasurements = curry(function (id, x) {
    return x.filter(propEq("organizationId", id));
  });
  var convertData = curry(function (measure, measurementWithMetadata) {
    var dateConvertedMeasurements = dateConvertMeasurements(measurementWithMetadata.measurements);
    var period = getPeriod(query.periodType, query.dateTo);
    var measurement = measurementByPeriod(period, dateConvertedMeasurements);
    var measurementValue = extractValue(null, measurement);
    return {
      partition: getGender(measure, measurementWithMetadata.measureId),
      dependent: extractValue(null, measurement),
      label: measurement === null || measurement === void 0 ? void 0 : measurement.reasonCode,
      helpText: !measurementValue && measureCodeExplanations(measurement === null || measurement === void 0 ? void 0 : measurement.reasonCode)
    };
  });
  var graphData = query.units.map(function (organizationId) {
    var _lookupOrganization, _getColorUsingOption, _lookupOrganization2;
    return {
      id: organizationId,
      name: (_lookupOrganization = lookupOrganization(organizationId)) === null || _lookupOrganization === void 0 ? void 0 : _lookupOrganization.name,
      color: (_getColorUsingOption = getColorUsingOption(query.colorSettings, activeUnitList, unitListIdx(organizationId), organizationId)) === null || _getColorUsingOption === void 0 ? void 0 : _getColorUsingOption.hex,
      drillable: (_lookupOrganization2 = lookupOrganization(organizationId)) === null || _lookupOrganization2 === void 0 ? void 0 : _lookupOrganization2.hasTail,
      data: pipe(orgMeasurements(organizationId), map(convertData(measure)), function (_data) {
        return sort(function (x) {
          return keys(allGenderTypes).findIndex(function (key) {
            return equals(x.partition, allGenderTypes[key]);
          });
        }, _data);
      })(measureMeasurementsWithMetadata)
    };
  });
  var translateDate = pipe(function (_date) {
    return arrayAnnotationDate(query.periodType, _date);
  }, function (_arrayDate) {
    return truncatedPeriodAnnotation(query.periodType, _arrayDate, true);
  });
  var translatedPeriod = translateDate(query.dateTo);
  var sortedData = sortGraphData(query.sort, graphData);
  var handleSort = function handleSort(column) {
    return setQuery(function (old) {
      return _objectSpread(_objectSpread({}, old), {}, {
        sort: column.sortOptions.includes(old.sort) ? column.sortOptions.find(function (x) {
          return !equals(old.sort, x);
        }) : column.sortOptions[0]
      });
    });
  };
  var drilldown = useCallback(function (organizationId) {
    return getChildOrganizations(measure.measureId, organizationId, authTicket).then(function (result) {
      return setUnitLists(pipe(function (_unitLists) {
        return copyUnitsIntoVolatileList(_unitLists, pluck("id", result).toObjectEntries("id"));
      }, function (_unitLists) {
        return selectUnitList(_unitLists, getVolatileId(_unitLists));
      }));
    });
  }, [measure.measureId, setUnitLists, authTicket]);
  return /*#__PURE__*/_jsx(StackedBarchart, {
    id: id,
    loading: loading,
    sort: query.sort,
    updateSort: handleSort,
    remove: function remove(id) {
      return setUnitLists(function (old) {
        return removeUnitsFromSelectedList(old, [id]);
      });
    },
    drilldown: drilldown,
    measureUnit: measure.measureUnit,
    drilldownLabel: "V\xE4lj enheter inom: ",
    tableCaption: "Rapporterade v\xE4rden f\xF6r dina valda enheter:",
    defaultText: "Din enhetslista \xE4r tom, l\xE4gg till enheter eller v\xE4lj en annan lista i kontrollpanelen.",
    colors: ["#00A9BF", "#406E75"],
    data: sortedData,
    style: {
      marginBottom: "20px"
    },
    columns: [{
      id: "alpha",
      label: "Enheter",
      sortOptions: ["alpha", "reverseAlpha"]
    }, {
      id: translatedPeriod,
      label: translatedPeriod,
      sortOptions: ["desc", "asc"]
    }]
  });
}